<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { NSpace, NButton, NInputNumber, NText, NProgress } from 'naive-ui';
import { formatDuration } from '@awesomeclocks/utils';
import type { TimerWidgetConfig } from '@awesomeclocks/types';
import WidgetWrapper from './WidgetWrapper.vue';

interface Props {
  widget: TimerWidgetConfig;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  remove: [];
  updateName: [name: string];
  updateSettings: [settings: TimerWidgetConfig['settings']];
  updateState: [state: TimerWidgetConfig['state']];
}>();

const remaining = ref(props.widget.state?.remaining ?? props.widget.settings.duration);
const isRunning = ref(props.widget.state?.isRunning ?? false);
const isPaused = ref(props.widget.state?.isPaused ?? false);
let intervalId: number | null = null;

const durationHours = computed({
  get: () => Math.floor(props.widget.settings.duration / 3600),
  set: (value: number | null) => {
    if (value === null || Number.isNaN(value)) {
      return;
    }
    const hours = Math.max(0, Math.round(value));
    const minutes = durationMinutes.value;
    const seconds = durationSeconds.value;
    updateDuration(hours, minutes, seconds);
  },
});

const durationMinutes = computed({
  get: () => Math.floor((props.widget.settings.duration % 3600) / 60),
  set: (value: number | null) => {
    if (value === null || Number.isNaN(value)) {
      return;
    }
    const minutes = Math.min(59, Math.max(0, Math.round(value)));
    const hours = durationHours.value;
    const seconds = durationSeconds.value;
    updateDuration(hours, minutes, seconds);
  },
});

const durationSeconds = computed({
  get: () => Math.floor(props.widget.settings.duration % 60),
  set: (value: number | null) => {
    if (value === null || Number.isNaN(value)) {
      return;
    }
    const seconds = Math.min(59, Math.max(0, Math.round(value)));
    const hours = durationHours.value;
    const minutes = durationMinutes.value;
    updateDuration(hours, minutes, seconds);
  },
});

const updateDuration = (hours: number, minutes: number, seconds: number) => {
  const newDuration = Math.max(0, hours * 3600 + minutes * 60 + seconds);
  emit('updateSettings', { ...props.widget.settings, duration: newDuration });
  if (!isRunning.value) {
    remaining.value = newDuration;
  }
};

const displayTime = computed(() => formatDuration(remaining.value));

const progress = computed(() => {
  const duration = props.widget.settings.duration;
  if (duration <= 0) {
    return 0;
  }
  return ((duration - remaining.value) / duration) * 100;
});

const start = () => {
  if (!isRunning.value) {
    isRunning.value = true;
    isPaused.value = false;
    emitState();
    startInterval();
  }
};

const pause = () => {
  if (isRunning.value && !isPaused.value) {
    isPaused.value = true;
    stopInterval();
    emitState();
  }
};

const resume = () => {
  if (isRunning.value && isPaused.value) {
    isPaused.value = false;
    emitState();
    startInterval();
  }
};

const reset = () => {
  stopInterval();
  isRunning.value = false;
  isPaused.value = false;
  remaining.value = props.widget.settings.duration;
  emitState();
};

const startInterval = () => {
  if (intervalId !== null) return;

  intervalId = window.setInterval(() => {
    if (remaining.value > 0) {
      remaining.value--;
      emitState();
    } else {
      // Timer finished
      stopInterval();
      isRunning.value = false;
      isPaused.value = false;
      playSound();
      emitState();
    }
  }, 1000);
};

const stopInterval = () => {
  if (intervalId !== null) {
    clearInterval(intervalId);
    intervalId = null;
  }
};

const playSound = () => {
  // Play sound notification
  if ('Notification' in window && Notification.permission === 'granted') {
    new Notification('Timer Finished', {
      body: `${props.widget.name} has completed!`,
    });
  }
};

const emitState = () => {
  emit('updateState', {
    remaining: remaining.value,
    isRunning: isRunning.value,
    isPaused: isPaused.value,
  });
};

onUnmounted(() => {
  stopInterval();
});

onMounted(() => {
  if (isRunning.value && !isPaused.value && remaining.value > 0) {
    startInterval();
  }
});

// Watch for duration changes
watch(
  () => props.widget.settings.duration,
  (newDuration) => {
    if (!isRunning.value) {
      remaining.value = newDuration;
    }
  }
);
</script>

<template>
  <WidgetWrapper
    :widget="widget"
    @remove="emit('remove')"
    @update-name="emit('updateName', $event)"
  >
    <NSpace vertical align="center" :size="20" style="width: 100%">
      <!-- Timer Display -->
      <div class="timer-display">
        <NProgress
          type="circle"
          :percentage="progress"
          :stroke-width="8"
          :show-indicator="false"
          :color="remaining === 0 ? '#f56c6c' : '#409eff'"
          style="width: 200px; height: 200px"
        />
        <div class="timer-overlay">
          <NText class="timer-readout" :depth="1">{{ displayTime }}</NText>
        </div>
      </div>

      <!-- Controls -->
      <NSpace>
        <NButton
          v-if="!isRunning"
          type="primary"
          size="large"
          :disabled="remaining === 0"
          @click="start"
        >
          Start
        </NButton>
        <NButton v-else-if="!isPaused" size="large" @click="pause"> Pause </NButton>
        <NButton v-else type="primary" size="large" @click="resume"> Resume </NButton>
        <NButton size="large" @click="reset"> Reset </NButton>
      </NSpace>

      <!-- Settings -->
      <NSpace vertical :size="8" class="settings">
        <NText>Duration</NText>
        <NSpace align="center" justify="space-between">
          <NText>Hours:</NText>
          <NInputNumber
            v-model:value="durationHours"
            :min="0"
            :max="99"
            size="small"
            :disabled="isRunning"
            style="width: 100px"
          />
        </NSpace>
        <NSpace align="center" justify="space-between">
          <NText>Minutes:</NText>
          <NInputNumber
            v-model:value="durationMinutes"
            :min="0"
            :max="59"
            size="small"
            :disabled="isRunning"
            style="width: 100px"
          />
        </NSpace>
        <NSpace align="center" justify="space-between">
          <NText>Seconds:</NText>
          <NInputNumber
            v-model:value="durationSeconds"
            :min="0"
            :max="59"
            size="small"
            :disabled="isRunning"
            style="width: 100px"
          />
        </NSpace>
      </NSpace>
    </NSpace>
  </WidgetWrapper>
</template>

<style scoped>
.timer-display {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.timer-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.timer-readout {
  font-size: 2.4rem;
  font-weight: 300;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.04em;
}

.settings {
  width: 100%;
  max-width: 300px;
  padding: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}
</style>
